/**
 * Minimal, dependency-free CSV READER — the inverse of lib/csv.ts (the RFC-4180 writer). It exists so
 * the client-import flow can parse an uploaded file ENTIRELY in the browser and POST the rows as a JSON
 * array (there is no @fastify/multipart on the server, by design). Correctness over cleverness.
 *
 * RFC-4180 it honours:
 *   - fields are comma-separated, rows are CRLF or LF terminated (both tolerated, mixed is fine);
 *   - a field MAY be wrapped in double-quotes; inside a quoted field a comma, a CR/LF and a doubled
 *     double-quote ("") are all literal data — so a single cell can carry commas and newlines;
 *   - a leading UTF-8 BOM (which our own writer emits for Excel) is stripped before parsing.
 *
 * What it deliberately does NOT do: no type coercion, no header logic, no trimming of cell whitespace
 * (the import mapper decides what to trim). It returns a rectangular-ish array of string rows; ragged
 * rows are returned as-is and the mapper tolerates short/long rows by index.
 */

/**
 * Parse CSV text into an array of rows, each row an array of string cells. A trailing newline does NOT
 * produce a spurious empty final row; a genuinely empty trailing field (e.g. "a,b,") IS preserved.
 */
export function parseCsv(text: string): string[][] {
  // Strip a leading UTF-8 BOM if present (Excel writes one; our own downloadCsv emits one too).
  let input = text;
  if (input.charCodeAt(0) === 0xfeff) input = input.slice(1);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let sawAnyChar = false; // did this row receive ANY content (so a trailing "\n" doesn't add a blank row)

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
    sawAnyChar = false;
  };

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!;

    if (inQuotes) {
      if (ch === '"') {
        // A doubled quote inside a quoted field is a literal quote; otherwise the quote closes the field.
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      sawAnyChar = true;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      sawAnyChar = true;
      continue;
    }
    if (ch === ',') {
      pushField();
      sawAnyChar = true;
      continue;
    }
    if (ch === '\r') {
      // Swallow CRLF as a single terminator; a lone CR also terminates.
      if (input[i + 1] === '\n') i++;
      pushRow();
      continue;
    }
    if (ch === '\n') {
      pushRow();
      continue;
    }
    field += ch;
    sawAnyChar = true;
  }

  // Flush the final field/row unless the input ended exactly on a row terminator (no dangling blank row).
  if (sawAnyChar || field.length > 0 || row.length > 0) {
    pushField();
    rows.push(row);
  }

  return rows;
}
